import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars-2";
import { SidebarData } from "../../data/json/sidebarData"; // Adjust import path as needed
import ImageWithBasePath from "../imageWithBasePath";
import { useDispatch, useSelector } from "react-redux";
import { setExpandMenu } from "../../data/redux/commonSlice";
import { jwtDecode } from "jwt-decode";
import apiDefinitions from "../../../api/apiDefinitions";
import { all_routes } from "../../../feature-module/router/all_routes";

interface DecodedToken {
  exp: number;
  serial_number: string;
  name: string;
  role_id: number;
  [key: string]: any;
}

interface SidebarSubmenuItem {
  label: string;
  link: string;
}

interface SidebarMenuItem {
  label: string;
  icon: string;
  submenu: boolean;
  showSubRoute: boolean;
  submenuItems: SidebarSubmenuItem[];
  link?: string;
}

interface SidebarMainItem {
  label: string;
  submenuOpen: boolean;
  showSubRoute: boolean;
  submenuHdr: string;
  submenuItems: SidebarMenuItem[];
}

const Sidebar: React.FC = () => {
  const route = all_routes;
  const location = useLocation();
  const expandMenu = useSelector((state: any) => state.expandMenu);
  const dispatch = useDispatch();

  const [employeeName, setEmployeeName] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [profilePic, setProfilePic] = useState(
    "assets/img/profiles/avatar-14.jpg"
  );
  const [department, setDepartment] = useState("");
  const [subOpen, setSubOpen] = useState<string | null>(null);
  const [subsidebar, setSubSidebar] = useState<string | null>(null); // Fixed line
  const [sidebarData, setSidebarData] = useState<SidebarMainItem[]>([]);
  const [roleId, setRoleId] = useState<number | undefined>(undefined);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      setSidebarData(SidebarData(undefined)); // Pass undefined when no token
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token);
      setEmployeeId(decodedToken.serial_number);
      setRoleId(decodedToken.role_id); // Store role_id in state
      setSidebarData(SidebarData(decodedToken.role_id)); // Pass role_id to SidebarData
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
      setSidebarData(SidebarData(undefined)); // Fallback to default menu on error
    }
  }, []);

  useEffect(() => {
    if (employeeId) {
      apiDefinitions
        .getProfilePic(employeeId)
        .then((response: any) => {
          if (response.data.code === 200) {
            setProfilePic(response.data.data.profilePictureUrl);
            setEmployeeName(response.data.data.nameWithInitials);
            setDepartment(response.data.data.department);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch profile picture", error);
        });
    }
  }, [employeeId]);

  const handleSidebarToggle = (menu: string) => {
    setSubOpen((prev) => (prev === menu ? null : menu));
  };

  const handleSubSidebarToggle = (submenu: string) => {
    setSubSidebar((prev) => (prev === submenu ? null : submenu));
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div
      className="sidebar"
      id="sidebar"
      onMouseEnter={() => dispatch(setExpandMenu(true))}
      onMouseLeave={() => dispatch(setExpandMenu(false))}
    >
      <Scrollbars>
        <div className="sidebar-inner slimscroll">
          <div id="sidebar-menu" className="sidebar-menu">
            {/* Show profile picture section only if roleId !== 5 */}
            {roleId !== 5 && (
              <ul>
                <li className="clinicdropdown theme">
                  <Link to={route.profile}>
                    <img
                      src={profilePic}
                      className="img-fluid profile-image"
                      alt="Profile"
                    />
                    <div className="user-names">
                      <h5>{employeeName}</h5>
                      <h6>{department}</h6>
                    </div>
                  </Link>
                </li>
              </ul>
            )}

            <ul>
              {sidebarData.map((mainLabel: SidebarMainItem, index: number) => (
                <li className="clinicdropdown" key={index}>
                  <h6 className="submenu-hdr">{mainLabel.label}</h6>
                  <ul>
                    {mainLabel.submenuItems.map((submenu: SidebarMenuItem) => (
                      <li
                        className={`submenu ${
                          subOpen === submenu.label ? "active" : ""
                        }`}
                        key={submenu.label}
                      >
                        <Link
                          to={submenu.link || "#"}
                          onClick={() => handleSidebarToggle(submenu.label)}
                          className={`${
                            isActive(submenu.link || "") ? "active" : ""
                          }`}
                        >
                          <i className={submenu.icon}></i>
                          <span>{submenu.label}</span>
                          {submenu.submenuItems && (
                            <span className="menu-arrow" />
                          )}
                        </Link>
                        {submenu.submenuItems && (
                          <ul
                            style={{
                              display:
                                subOpen === submenu.label ? "block" : "none",
                            }}
                          >
                            {submenu.submenuItems.map(
                              (item: SidebarSubmenuItem) => (
                                <li key={item.label}>
                                  <Link
                                    to={item.link}
                                    className={`${
                                      isActive(item.link) ? "active" : ""
                                    }`}
                                    onClick={() =>
                                      handleSubSidebarToggle(item.label)
                                    }
                                  >
                                    {item.label}
                                  </Link>
                                </li>
                              )
                            )}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Scrollbars>
    </div>
  );
};

export default Sidebar;
